import type { CanvasPresenceScope } from '@/lib/collaboration/canvas-presence-scope';
import {
  useCollaborationPresenceStore,
  type CollaborationPresenceContext,
  type CollaborationPresenceTarget,
  type CollaborationPresenceTargetType,
  type CollaborationPresenceUser,
} from '@/stores/collaborationPresenceStore';
import type { RealtimeEvent } from '@/types/realtime';

const TARGET_TYPES = new Set<CollaborationPresenceTargetType>([
  'storyboard',
  'resource_element',
  'resource_tag',
  'project_resource',
  'storyboard_item',
]);

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function readContext(value: unknown): CollaborationPresenceContext | null {
  return value === 'project_page' || value === 'parse_flow' || value === 'canvas' ? value : null;
}

function readCanvasScope(value: unknown): CanvasPresenceScope | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const contextType = readString(record.contextType);
  if (contextType !== 'storyboard' && contextType !== 'asset') return null;
  return {
    contextType,
    storyboardId: readString(record.storyboardId),
    assetId: readString(record.assetId),
    assetType: readString(record.assetType),
  };
}

function readOrgKind(value: unknown): CollaborationPresenceUser['orgKind'] {
  return value === 'host' || value === 'contractor' ? value : null;
}

function readTarget(value: unknown): CollaborationPresenceTarget | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const type = readString(record.type);
  const id = readString(record.id);
  if (!type || !id || !TARGET_TYPES.has(type as CollaborationPresenceTargetType)) return null;
  return {
    type: type as CollaborationPresenceTargetType,
    id,
    storyboardId: readString(record.storyboardId),
    resourceType: readString(record.resourceType),
  };
}

function readUser(
  value: unknown,
  fallbackProjectId?: string | null
): CollaborationPresenceUser | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const userId = readString(record.userId);
  const projectId = readString(record.projectId) ?? fallbackProjectId;
  const pageContext = readContext(record.pageContext);
  if (!userId || !projectId || !pageContext) return null;
  return {
    userId,
    displayName: readString(record.displayName),
    avatarUrl: readString(record.avatarUrl),
    orgKind: readOrgKind(record.orgKind),
    projectId,
    pageContext,
    canvasScope: readCanvasScope(record.canvasScope),
    selectedTarget: readTarget(record.selectedTarget),
    updatedAt:
      readString(record.updatedAt) ?? readString(record.occurredAt) ?? new Date().toISOString(),
  };
}

function readUsers(value: unknown, fallbackProjectId?: string | null): CollaborationPresenceUser[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => readUser(item, fallbackProjectId))
    .filter((item): item is CollaborationPresenceUser => Boolean(item));
}

export function handleProjectPresenceRealtimeEvent(event: RealtimeEvent): void {
  if (event.topic !== 'project_presence') return;
  const projectId = readString(event.payload.projectId);
  if (!projectId) return;

  const store = useCollaborationPresenceStore.getState();
  if (event.event === 'snapshot') {
    store.replaceProjectUsers(projectId, readUsers(event.payload.onlineUsers, projectId));
    return;
  }

  if (Object.prototype.hasOwnProperty.call(event.payload, 'onlineUsers')) {
    store.replaceProjectUsers(projectId, readUsers(event.payload.onlineUsers, projectId));
    if (event.event === 'user_left') {
      const user = readUser(event.payload.user, projectId);
      if (user) {
        store.removeUser(projectId, user.userId);
      }
      return;
    }
    if (event.event === 'selection_changed') {
      const user = readUser(event.payload.user, projectId);
      if (user) {
        store.upsertUser(user);
      }
      return;
    }
    if (event.event === 'user_joined') {
      return;
    }
  }

  const user = readUser(event.payload.user, projectId);
  if (!user) return;
  if (event.event === 'user_left') {
    store.removeUser(projectId, user.userId);
    return;
  }
  if (event.event === 'user_joined' || event.event === 'selection_changed') {
    store.upsertUser(user);
  }
}
